import { CheckCircle2, XCircle, BookOpen } from "lucide-react";

const CARD = "min-w-0 border border-[var(--iddl-rule)] bg-white p-4 text-sm";
const HEAD = "font-display text-base font-semibold text-[var(--iddl-ink)] pb-2 mb-3 border-b border-[var(--iddl-rule)]";
const OK = "border-emerald-300 bg-emerald-50 text-emerald-800";
const BAD = "border-rose-300 bg-rose-50 text-rose-700";
const NEUTRAL = "border-[var(--iddl-rule)] bg-[var(--iddl-surface)] text-[var(--iddl-ink)]";

const Label = ({ children, className = "" }) => (
  <div className={`label-mono text-[var(--iddl-muted)] mb-1.5 ${className}`}>{children}</div>
);

const Chip = ({ tone, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 border text-xs font-mono-disp break-all ${tone}`}>
    {children}
  </span>
);

const ChipRow = ({ children, empty = "None" }) => (
  <div className="flex flex-wrap gap-1.5">
    {children && children.length ? children : <span className="text-xs text-[var(--iddl-muted)]">{empty}</span>}
  </div>
);

function AnswerBox({ ok, label, text, testid }) {
  return (
    <div data-testid={testid}>
      <Label>{label}</Label>
      <div className={`flex items-start gap-2 p-2.5 border leading-relaxed ${ok ? OK : BAD}`}>
        {ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />}
        <span className="min-w-0 break-words">{text || "— not answered —"}</span>
      </div>
    </div>
  );
}

function KnowCol({ cid, items }) {
  return (
    <section className={CARD} data-testid={`admin-review-col-k-${cid}`}>
      <h4 className={HEAD}>Know · knowledge</h4>
      <div className="grid gap-4">
        {items.map((k, i) => (
          <div key={i} className="grid gap-3" data-testid={`admin-review-k-${cid}-${i}`}>
            <div>
              <Label>Question</Label>
              <p className="font-semibold leading-relaxed break-words">{k.question}</p>
            </div>
            <AnswerBox ok={k.is_correct} label="Candidate's answer" text={k.submitted_option} testid={`admin-review-k-candidate-${cid}-${i}`} />
            <AnswerBox ok label="Correct answer" text={k.correct_option} testid={`admin-review-k-correct-${cid}-${i}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

function PerformCol({ cid, cap }) {
  const expectedNames = new Set((cap.expected || []).map((e) => e.event));
  const recorded = cap.recorded_events || [];
  const autoFail = cap.auto_fail_hit || [];
  return (
    <section className={CARD} data-testid={`admin-review-col-c-${cid}`}>
      <h4 className={HEAD}>Perform · behaviour</h4>
      {cap.task && <p className="text-[var(--iddl-muted)] leading-relaxed break-words mb-4">{cap.task}</p>}
      <div className="grid gap-4">
        <div data-testid={`admin-review-c-expected-${cid}`}>
          <Label>Expected behaviours</Label>
          <ChipRow>
            {(cap.expected || []).map((e, i) => (
              <Chip key={i} tone={e.done ? OK : BAD}>
                {e.done ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                {e.event}<span className="opacity-70">· {e.kind}</span>
              </Chip>
            ))}
          </ChipRow>
          {cap.valid_final_decisions?.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-[var(--iddl-muted)]">Valid final decisions:</span>
              {cap.valid_final_decisions.map((v) => <Chip key={v} tone={NEUTRAL}>{v}</Chip>)}
            </div>
          )}
        </div>
        <div className="pt-3 border-t border-[var(--iddl-rule)]" data-testid={`admin-review-c-events-${cid}`}>
          <Label>Candidate's recorded events</Label>
          <ChipRow empty="— no behaviour events recorded —">
            {recorded.map((e, i) => <Chip key={i} tone={expectedNames.has(e) ? OK : NEUTRAL}>{e}</Chip>)}
          </ChipRow>
          {cap.final_decision && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-[var(--iddl-muted)]">Final decision submitted:</span>
              <Chip tone={(cap.valid_final_decisions || []).includes(cap.final_decision) ? OK : BAD}>{cap.final_decision}</Chip>
            </div>
          )}
        </div>
        <div className="pt-3 border-t border-[var(--iddl-rule)]" data-testid={`admin-review-c-autofail-${cid}`}>
          <Label className={autoFail.length ? "!text-rose-700" : ""}>Auto-fail triggers hit</Label>
          <ChipRow>{autoFail.map((a) => <Chip key={a} tone={BAD}>{a}</Chip>)}</ChipRow>
        </div>
      </div>
    </section>
  );
}

function ReasonCol({ cid, refl }) {
  const themes = refl.themes || [];
  const hit = themes.filter((t) => t.matched).length;
  return (
    <section className={CARD} data-testid={`admin-review-col-r-${cid}`}>
      <h4 className={HEAD}>Reason · reflection</h4>
      {refl.prompt && <p className="text-[var(--iddl-muted)] italic leading-relaxed break-words mb-4">{refl.prompt}</p>}
      <div className="grid gap-4">
        <div data-testid={`admin-review-r-themes-${cid}`}>
          <Label>Required themes · {hit}/{themes.length} covered</Label>
          <ChipRow>
            {themes.map((t, i) => (
              <Chip key={i} tone={t.matched ? OK : BAD} testid={`admin-review-theme-${cid}-${i}`}>
                {t.matched ? <CheckCircle2 size={11} /> : <XCircle size={11} />}{t.label}
              </Chip>
            ))}
          </ChipRow>
        </div>
        <div className="pt-3 border-t border-[var(--iddl-rule)]" data-testid={`admin-review-reflection-${cid}`}>
          <Label>Candidate's reflection</Label>
          {refl.submitted_text
            ? <p className="whitespace-pre-wrap break-words leading-relaxed p-3 bg-[var(--iddl-surface)] border border-[var(--iddl-rule)]">{refl.submitted_text}</p>
            : <p className="text-xs text-[var(--iddl-muted)]">— No reflection submitted.</p>}
        </div>
      </div>
    </section>
  );
}

export function ConceptReview({ title, r }) {
  const ma = r.model_answers;
  if (!ma) return null;
  return (
    <div className="mt-5" data-testid={`admin-review-${r.concept_id}`}>
      <div className="label-mono flex items-center gap-2 mb-3"><BookOpen size={12} /> {title}</div>
      <div className="grid xl:grid-cols-3 gap-4 items-start">
        <KnowCol cid={r.concept_id} items={ma.knowledge || []} />
        <PerformCol cid={r.concept_id} cap={ma.capability || {}} />
        <ReasonCol cid={r.concept_id} refl={ma.reflection || {}} />
      </div>
    </div>
  );
}
